import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatINR } from "@/lib/constants";
import {
  Plus, Users, BarChart3, Loader2, Building2, Eye, RefreshCw,
} from "lucide-react";

export default function AdminChannels() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", code: "", commission_type: "percentage", commission_rate: "0",
    description: "", owner_name: "", owner_email: "", owner_mobile: "", owner_password: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/channels");
      setChannels(data.channels || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load channels");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    if (!form.name || !form.code || !form.owner_email || !form.owner_mobile || !form.owner_password) {
      toast.error("All owner fields are required"); return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name, code: form.code.toUpperCase(),
        commission_type: form.commission_type,
        commission_config: { rate: parseFloat(form.commission_rate) || 0 },
        description: form.description,
        owner_name: form.owner_name, owner_email: form.owner_email,
        owner_mobile: form.owner_mobile, owner_password: form.owner_password,
      };
      const { data } = await api.post("/admin/channels", payload);
      toast.success(data.message || "Channel created");
      setCreateOpen(false);
      setForm({ name: "", code: "", commission_type: "percentage", commission_rate: "0", description: "", owner_name: "", owner_email: "", owner_mobile: "", owner_password: "" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create channel");
    } finally { setBusy(false); }
  };

  const viewDetail = async (channelId) => {
    try {
      const { data } = await api.get(`/admin/channels/${channelId}`);
      setDetail(data);
      setDetailOpen(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load channel");
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Admin · Channels</span>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-2 flex items-center gap-2">
            <Building2 className="h-7 w-7 text-primary" strokeWidth={1.5} />
            Business Channels
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl text-sm">
            Create channel partners who can onboard and manage their own sub-partners.
            Track all activity and configure commission per channel.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-channel-btn">
          <Plus className="h-4 w-4 mr-1" /> New channel
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
        <div className="surface-card p-4" data-testid="kpi-total-channels">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Channels</div>
          <div className="font-display text-2xl font-bold mt-1">{channels.length}</div>
        </div>
        <div className="surface-card p-4" data-testid="kpi-total-sub-partners">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Sub-partners</div>
          <div className="font-display text-2xl font-bold mt-1">{channels.reduce((s, c) => s + (c.stats?.sub_partner_count || 0), 0)}</div>
        </div>
        <div className="surface-card p-4" data-testid="kpi-total-txns">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Transactions</div>
          <div className="font-display text-2xl font-bold mt-1">{channels.reduce((s, c) => s + (c.stats?.txn_count || 0), 0)}</div>
        </div>
        <div className="surface-card p-4" data-testid="kpi-total-volume">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Volume</div>
          <div className="font-display text-2xl font-bold mt-1">{formatINR(channels.reduce((s, c) => s + (c.stats?.total_volume || 0), 0))}</div>
        </div>
      </div>

      {/* Channels table */}
      <div className="mt-6 surface-card overflow-hidden" data-testid="channels-table">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Loading…</div>
        ) : channels.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-muted-foreground">No channels yet. Create your first business channel.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="p-3 pl-5">Channel</th>
                  <th className="p-3">Code</th>
                  <th className="p-3">Owner</th>
                  <th className="p-3">Commission</th>
                  <th className="p-3 text-center">Sub-partners</th>
                  <th className="p-3 text-center">Txns</th>
                  <th className="p-3 text-right">Volume</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right pr-5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {channels.map((ch) => (
                  <tr key={ch.id} className="border-b border-border/50 last:border-0 hover:bg-accent/30" data-testid={`channel-row-${ch.code}`}>
                    <td className="p-3 pl-5">
                      <div className="font-medium">{ch.name}</div>
                      {ch.description && <div className="text-[10px] text-muted-foreground mt-0.5">{ch.description}</div>}
                    </td>
                    <td className="p-3 font-mono text-xs">{ch.code}</td>
                    <td className="p-3">
                      <div className="text-sm">{ch.owner?.full_name || "—"}</div>
                      <div className="text-[10px] text-muted-foreground">{ch.owner?.email}</div>
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {ch.commission_type === "percentage" ? `${ch.commission_config?.rate || 0}%` : ch.commission_type}
                      </Badge>
                    </td>
                    <td className="p-3 text-center font-mono">{ch.stats?.sub_partner_count || 0}</td>
                    <td className="p-3 text-center font-mono">{ch.stats?.txn_count || 0}</td>
                    <td className="p-3 text-right font-mono">{formatINR(ch.stats?.total_volume || 0)}</td>
                    <td className="p-3">
                      <Badge variant={ch.status === "active" ? "default" : "outline"} className={ch.status === "active" ? "bg-success text-success-foreground" : ""}>
                        {ch.status}
                      </Badge>
                    </td>
                    <td className="p-3 pr-5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => viewDetail(ch.id)} data-testid={`view-channel-${ch.code}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create channel dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create business channel</DialogTitle>
            <DialogDescription>Creates a channel and its owner account (role: channel_partner). The owner can then create sub-partners.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Channel name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ABC Distributors" data-testid="channel-name" />
              </div>
              <div>
                <Label>Channel code</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "") })} placeholder="ABC001" data-testid="channel-code" className="font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Commission type</Label>
                <Select value={form.commission_type} onValueChange={(v) => setForm({ ...form, commission_type: v })}>
                  <SelectTrigger data-testid="channel-commission-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="flat">Flat (per txn)</SelectItem>
                    <SelectItem value="slab">Slab-based</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rate</Label>
                <Input type="number" step="0.01" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} placeholder="0.5" data-testid="channel-commission-rate" />
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Channel description…" rows={2} />
            </div>
            <div className="border-t border-border pt-4">
              <h4 className="text-sm font-semibold mb-3">Channel owner (partner login)</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Full name</Label>
                  <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="John Doe" data-testid="channel-owner-name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} placeholder="john@abc.com" data-testid="channel-owner-email" />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <Input value={form.owner_mobile} onChange={(e) => setForm({ ...form, owner_mobile: e.target.value })} placeholder="9876543210" data-testid="channel-owner-mobile" />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input type="password" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} placeholder="Min 8 chars" data-testid="channel-owner-password" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={onCreate} disabled={busy} data-testid="create-channel-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Create channel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Channel detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  {detail.channel.name}
                  <Badge variant="outline" className="ml-2 font-mono">{detail.channel.code}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                <div className="surface-card p-3">
                  <div className="text-[10px] uppercase text-muted-foreground">Sub-partners</div>
                  <div className="text-xl font-bold mt-1">{detail.stats?.sub_partner_count || 0}</div>
                </div>
                <div className="surface-card p-3">
                  <div className="text-[10px] uppercase text-muted-foreground">Transactions</div>
                  <div className="text-xl font-bold mt-1">{detail.stats?.txn_count || 0}</div>
                </div>
                <div className="surface-card p-3">
                  <div className="text-[10px] uppercase text-muted-foreground">Volume</div>
                  <div className="text-xl font-bold mt-1">{formatINR(detail.stats?.total_volume || 0)}</div>
                </div>
                <div className="surface-card p-3">
                  <div className="text-[10px] uppercase text-muted-foreground">Commission</div>
                  <div className="text-xl font-bold mt-1">{detail.channel.commission_config?.rate || 0}%</div>
                </div>
              </div>
              {detail.owner && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold mb-2">Channel owner</h4>
                  <div className="text-sm">{detail.owner.full_name} · {detail.owner.email}</div>
                </div>
              )}
              {detail.sub_partners?.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold mb-2">Sub-partners ({detail.sub_partners.length})</h4>
                  <div className="space-y-1">
                    {detail.sub_partners.map((sp) => (
                      <div key={sp.id} className="flex items-center justify-between px-3 py-2 rounded border border-border/50 text-sm">
                        <div>
                          <span className="font-medium">{sp.full_name}</span>
                          <span className="ml-2 text-xs text-muted-foreground">{sp.email}</span>
                        </div>
                        <Badge variant="outline" className="text-[10px]">{sp.account_status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
